import React from 'react';
import { Link } from 'react-router-dom';
import classes from './Home.module.css';
import Seo from './components/Seo';
import Header from './components/Header';
import Footer from './components/Footer';
import LotusLogo from './components/LotusLogo';
import StoreButtons from './components/StoreButtons';
import { useI18n } from './i18n/LanguageContext';
import { useContent } from './content';
import { slugFor } from './content/slugs';

/** Feature icons are locale-independent, so they pair with the strings by index. */
const FEATURE_ICONS = ['🌸', '📜', '🎯', '🖼️', '🌙', '✨', '🔔', '🎨'];

function Home() {
  const { t, path, locale, strings } = useI18n();
  const { posts } = useContent();

  const features = strings.home.features.items;
  const steps = strings.home.how.steps;
  const faqItems = strings.home.faq.items;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const featuredPosts = posts.slice(0, 3);

  return (
    <div className={classes.page}>
      <Seo
        title={t('meta.home.title')}
        description={t('meta.home.description')}
        routePath="/"
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className={classes.hero}>
          <div className={classes.logoBox}>
            <LotusLogo />
          </div>

          <h1 className={classes.title}>
            {t('home.hero.titleLine1')} <br />
            <span className={classes.gradientText}>{t('home.hero.titleAccent')}</span>
          </h1>

          <p className={classes.subtitle}>{t('home.hero.subtitle')}</p>

          <StoreButtons />

          <p className={classes.heroNote}>{t('home.hero.note')}</p>
        </section>

        {/* Features */}
        <section className={classes.section} id="features">
          <h2 className={classes.sectionTitle}>
            {t('home.features.titlePre')}
            <span className={classes.gradientText}>{t('home.features.titleAccent')}</span>
            {t('home.features.titlePost')}
          </h2>
          <p className={classes.sectionSubtitle}>{t('home.features.subtitle')}</p>
          <div className={classes.featureGrid}>
            {features.map((feature, i) => (
              <div key={feature.title} className={classes.featureCard}>
                <span className={classes.featureIcon} aria-hidden="true">
                  {FEATURE_ICONS[i]}
                </span>
                <h3 className={classes.featureTitle}>{feature.title}</h3>
                <p className={classes.featureText}>{feature.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className={classes.section}>
          <h2 className={classes.sectionTitle}>
            {t('home.how.titlePre')}
            <span className={classes.gradientText}>{t('home.how.titleAccent')}</span>
            {t('home.how.titlePost')}
          </h2>
          <div className={classes.stepsGrid}>
            {steps.map((step, i) => (
              <div key={step.title} className={classes.step}>
                <span className={classes.stepNumber}>{i + 1}</span>
                <h3 className={classes.stepTitle}>{step.title}</h3>
                <p className={classes.stepText}>{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Blog teaser */}
        <section className={classes.section}>
          <h2 className={classes.sectionTitle}>
            {t('home.blogTeaser.titlePre')}
            <span className={classes.gradientText}>{t('home.blogTeaser.titleAccent')}</span>
            {t('home.blogTeaser.titlePost')}
          </h2>
          <p className={classes.sectionSubtitle}>{t('home.blogTeaser.subtitle')}</p>
          <div className={classes.blogGrid}>
            {featuredPosts.map((post) => (
              <Link
                key={post.id}
                to={path(`/blog/${slugFor(post.id, locale)}`)}
                className={classes.blogCard}
              >
                <span className={classes.blogCategory}>{post.category}</span>
                <h3 className={classes.blogCardTitle}>{post.title}</h3>
                <p className={classes.blogCardExcerpt}>{post.excerpt}</p>
                <span className={classes.blogCardMeta}>
                  {t('blog.readTime', { n: post.readTime })}
                </span>
              </Link>
            ))}
          </div>
          <div className={classes.blogAllWrap}>
            <Link to={path('/blog')} className={classes.blogAllLink}>
              {t('home.blogTeaser.viewAll')}
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className={classes.section} id="faq">
          <h2 className={classes.sectionTitle}>
            {t('home.faq.titlePre')}
            <span className={classes.gradientText}>{t('home.faq.titleAccent')}</span>
            {t('home.faq.titlePost')}
          </h2>
          <div className={classes.faqList}>
            {faqItems.map((item, i) => (
              <details key={i} className={classes.faqItem}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Download CTA */}
        <section className={classes.downloadSection} id="download">
          <div className={classes.downloadCard}>
            <h2 className={classes.downloadTitle}>{t('home.download.title')}</h2>
            <p className={classes.downloadText}>{t('home.download.text')}</p>
            <StoreButtons />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Home;
